import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/** Shown when Firebase blocks SMS due to reCAPTCHA / app check (shared by phone link + MFA flows). */
export const PHONE_AUTH_RECAPTCHA_USER_MESSAGE =
  "Verification couldn't complete the security check for SMS. Try disabling your ad blocker for this site, switching to another browser, or completing verification on your phone.";

/**
 * Lightweight telemetry for Firebase Phone Auth failures (no extra user doc reads).
 * Fire-and-forget: never throws; safe to call from error mappers.
 */
export const logPhoneAuthFailure = async (error, phase = 'unknown') => {
  const code = error?.code ?? 'unknown';
  console.error('Phone auth error code:', code, '| phase:', phase, '| message:', error?.message ?? error);

  try {
    const user = auth.currentUser;
    await addDoc(collection(db, 'errorLogs'), {
      errorSummary: `Phone auth [${phase}]: ${code}`,
      errorType: 'PHONE_AUTH',
      context: `PhoneAuth:${phase}`,
      timestamp: serverTimestamp(),
      resolved: false,
      severity: 'LOW',
      userId: user?.uid ?? 'anonymous',
      userEmail: user?.email ?? 'unknown',
      userName: user?.displayName || user?.email?.split('@')[0] || 'unknown',
      errorDetails: {
        message: error?.message ?? '',
        code,
        phase,
        type: error?.constructor?.name ?? typeof error,
      },
      userContext: {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        url: typeof window !== 'undefined' ? window.location.href : '',
      },
      additionalData: { source: 'phoneAuthTelemetry' },
    });
  } catch (loggingError) {
    console.error('Failed to log phone auth telemetry to Firestore:', loggingError);
  }
};

/**
 * Log errors to Firestore for admin review
 * This allows you to see production errors without asking users for console logs
 */
export const logError = async (errorContext, error, additionalData = {}) => {
  try {
    const user = auth.currentUser;
    
    // Extract error details with better handling for different error types
    const errorDetails = {
      message: error?.message || error?.toString() || 'Unknown error',
      code: error?.code || error?.name || 'UNKNOWN',
      stack: error?.stack || null,
      type: error?.constructor?.name || typeof error,
      // For Firebase errors
      firebaseCode: error?.code || null,
      firebaseCustomData: error?.customData || null,
      // For fetch/network errors
      status: error?.status || null,
      statusText: error?.statusText || null
    };
    
    // Serialize the full error object
    let fullErrorObject = null;
    try {
      fullErrorObject = JSON.stringify(error, Object.getOwnPropertyNames(error));
    } catch (e) {
      fullErrorObject = 'Could not serialize error object';
    }
    
    // Generate human-readable error summary
    let errorSummary = errorDetails.message;
    if (additionalData.imageView) {
      errorSummary = `Image upload failed: ${additionalData.imageView} image`;
    } else if (errorContext.includes('Auth')) {
      errorSummary = `Authentication error: ${errorDetails.message}`;
    } else if (errorContext.includes('Payment')) {
      errorSummary = `Payment error: ${errorDetails.message}`;
    }
    
    // Categorize error type for filtering
    let errorType = 'UNKNOWN';
    if (errorContext.includes('Image')) {
      errorType = 'IMAGE_UPLOAD';
    } else if (errorContext.includes('Auth')) {
      errorType = 'AUTHENTICATION';
    } else if (errorContext.includes('Payment') || errorContext.includes('Stripe')) {
      errorType = 'PAYMENT';
    } else if (errorContext.includes('Submit') || errorContext.includes('Form')) {
      errorType = 'FORM_SUBMISSION';
    } else if (errorDetails.code?.includes('storage/')) {
      errorType = 'STORAGE';
    } else if (errorDetails.code?.includes('firestore/')) {
      errorType = 'DATABASE';
    }
    
    // Get user info (prioritize user doc data if available)
    let userName = 'unknown';
    try {
      if (user?.uid) {
        const { doc, getDoc } = await import('firebase/firestore');
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          userName = userDoc.data().username || userDoc.data().fullName || 'unknown';
        }
      }
    } catch (e) {
      // Silently fail, userName will stay 'unknown'
    }
    
    // Create error log document with ADMIN-FRIENDLY structure
    const errorLog = {
      // ⭐ TOP-LEVEL FIELDS FOR EASY ADMIN FILTERING/VIEWING
      errorSummary, // Human-readable one-liner
      errorType, // Category for filtering
      userId: user?.uid || 'anonymous',
      userEmail: user?.email || 'unknown',
      userName, // Username or full name
      context: errorContext,
      timestamp: serverTimestamp(),
      resolved: false,
      severity: errorDetails.code?.includes('permission-denied') || errorDetails.code?.includes('unauthenticated') ? 'HIGH' : 'MEDIUM',
      
      // Detailed information (nested for cleanliness)
      errorDetails,
      fullErrorObject,
      userContext: {
        emailVerified: user?.emailVerified || false,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        windowSize: `${window.innerWidth}x${window.innerHeight}`,
        online: navigator.onLine,
        cookiesEnabled: navigator.cookieEnabled,
        url: window.location.href,
        referrer: document.referrer || 'direct',
        deviceMemory: navigator.deviceMemory,
        hardwareConcurrency: navigator.hardwareConcurrency
      },
      additionalData // Any extra context from the calling code
    };
    
    // Log to Firestore
    await addDoc(collection(db, 'errorLogs'), errorLog);
    
    console.log('✅ Error logged to Firestore for admin review');
  } catch (loggingError) {
    // Don't let logging errors break the app
    console.error('❌ Failed to log error to Firestore:', loggingError);
    console.error('Original error:', error);
  }
};

/**
 * Log a custom event or warning (non-error scenarios)
 */
export const logEvent = async (eventType, eventData) => {
  try {
    const user = auth.currentUser;
    
    const eventLog = {
      eventType,
      eventData,
      userId: user?.uid || 'anonymous',
      email: user?.email || 'unknown',
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: serverTimestamp()
    };
    
    await addDoc(collection(db, 'eventLogs'), eventLog);
    console.log('✅ Event logged:', eventType);
  } catch (loggingError) {
    console.error('❌ Failed to log event:', loggingError);
  }
};
